export interface AssistantShellSnapshot {
    open: boolean
    generation: number
    shell: boolean
}

type Listener = () => void

let open = false
let generation = 0
let shellClaims = 0
let organizationId: string | null | undefined
let queuedPrompt: {generation: number, prompt: string} | null = null
let snapshot: AssistantShellSnapshot = {open: false, generation: 0, shell: false}
const listeners = new Set<Listener>()

const publish = (): void => {
    const shell = shellClaims > 0

    if (open === snapshot.open && generation === snapshot.generation && shell === snapshot.shell) {
        return
    }

    snapshot = {open, generation, shell}

    for (const listener of listeners) {
        listener()
    }
}

export const subscribeAssistantShell = (listener: Listener): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}

export const getAssistantShellSnapshot = (): AssistantShellSnapshot => snapshot

export const toggleAssistant = (): void => {
    open = !open
    publish()
}

/**
 * Opens a new thread and keeps the prompt until that generation reads it.
 * The snapshot stays open, generation, and shell.
 */
export const openAssistantPrompt = (prompt: string): void => {
    generation += 1
    open = true
    queuedPrompt = {generation, prompt}
    publish()
}

export const takeAssistantPrompt = (currentGeneration: number): string | null => {
    if (queuedPrompt?.generation !== currentGeneration) {
        return null
    }

    const prompt = queuedPrompt.prompt
    queuedPrompt = null

    return prompt
}

export const minimizeAssistant = (): void => {
    if (!open) {
        return
    }

    open = false
    publish()
}

/**
 * Full layout claims the shell while it is mounted. A visit between two
 * full pages releases and claims again before paint, so the chat stays.
 */
export const claimAppShell = (): (() => void) => {
    shellClaims += 1
    publish()

    return () => {
        shellClaims = Math.max(0, shellClaims - 1)
        publish()
    }
}

/**
 * The first organization is recorded. A later change closes the chat and
 * starts a new thread. The same organization leaves the thread alone.
 */
export const syncAssistantOrganization = (id: string | null): void => {
    if (organizationId === undefined) {
        organizationId = id

        return
    }

    if (id === organizationId) {
        return
    }

    organizationId = id
    open = false
    generation += 1
    queuedPrompt = null
    publish()
}

export const resetAssistantShell = (): void => {
    open = false
    generation = 0
    shellClaims = 0
    organizationId = undefined
    queuedPrompt = null
    snapshot = {open: false, generation: 0, shell: false}
    listeners.clear()
}
